import React, { useState, useEffect } from 'react';
import { Layout, Spin, Alert, Button } from 'antd';
import { AppProvider, useAppContext } from './context/AppContext';
import { FilterProvider, useFilterContext } from './context/FilterContext';
import MapContainer from './components/map/MapContainer';
import Sidebar from './components/layout/Sidebar';
import { RenderMode } from './types/index';
import { ActivityKey } from './components/routes/RouteTooltip';
import { useFilteredRoutes } from './hooks/useFilteredRoutes';
import { useRoutes } from './hooks/useRoutes';
import { useIsDesktop } from './hooks/common/useMediaQuery';

const { Content } = Layout;

const AppContent: React.FC = () => {
  const { routes, setRoutes, loading, setLoading, error, setError } = useAppContext();
  const { filters, setFilters } = useFilterContext();
  const isDesktop = useIsDesktop();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);
  const renderMode: RenderMode = 'leaflet-canvas';

  // Charger les routes quand l'activité change
  const selectedActivity = filters.activities.length > 0 ? filters.activities[0] : undefined;
  const { routes: fetchedRoutes, loading: routesLoading, error: routesError, refetch: refetchRoutes } = useRoutes(selectedActivity);

  // Synchroniser les routes chargées avec le contexte
  useEffect(() => {
    if (fetchedRoutes.length > 0) {
      setRoutes(fetchedRoutes);
      setLoading(false);
      setError(null);
    }
  }, [fetchedRoutes, setRoutes, setLoading, setError]);

  // Synchroniser le loading et l'erreur
  useEffect(() => {
    setLoading(routesLoading);
  }, [routesLoading, setLoading]);

  useEffect(() => {
    if (routesError) {
      setError(routesError);
    }
  }, [routesError, setError]);

  // Utilisation du hook personnalisé pour filtrer les routes
  const { filteredRoutes } = useFilteredRoutes(routes, filters);

  if (loading || routesLoading) {
    return (
      <div className="app-loading">
        <Spin size="large" tip="Chargement des routes..." />
      </div>
    );
  }

  if (error || routesError) {
    return (
      <div className="app-error">
        <Alert
          message="Erreur de chargement"
          description={error || routesError}
          type="error"
          showIcon
          action={
            <Button
              size="small"
              onClick={() => {
                refetchRoutes();
              }}
            >
              Réessayer
            </Button>
          }
        />
      </div>
    );
  }

  const activityKey = filters.activities[0] as ActivityKey | undefined;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar
        routes={routes}
        filters={filters}
        setFilters={setFilters}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />
      <Layout style={{ marginLeft: isDesktop ? 288 : 0, transition: 'margin-left 0.2s' }}>
        <Content style={{ height: '100vh', padding: 0, position: 'relative' }}>
          <MapContainer
            routes={filteredRoutes}
            allRoutes={routes}
            renderMode={renderMode}
            showRanges={false}
            selectedActivity={activityKey}
          />
        </Content>
      </Layout>
    </Layout>
  );
};

const App: React.FC = () => {
  return (
    <AppProvider>
      <FilterProvider>
        <AppContent />
      </FilterProvider>
    </AppProvider>
  );
};

export default App;
